import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Calendar, Pencil } from 'lucide-react';
import api, { describeSaveError } from '../../utils/authUtils';
import { mediaUrl } from '../../utils/mediaUrl';
import { useLanguage } from '../../contexts/LanguageContext';
import { getCountryOptions } from '../../utils/countries';
import { MIN_INTERESTS } from '../../constants/doctorProfile';
import ProfileEditModal from './ProfileEditModal';
import RecommendedStudies from './RecommendedStudies';

const API = process.env.REACT_APP_API_URL || '';
const BLUE = '#47ABDB';

// Same Hero/Grey layout as SpeakerDashboard.js (styled component values
// copied verbatim, not just approximated), so a person moving between their
// speaker hub and their attendee profile sees one consistent look - the same
// avatar, the same photo-over-the-hero overlap, the same card style. Below
// the hero, this page uses a 2x2 grid (see GreyInner) rather than
// SpeakerDashboard's simpler two-column split.
const Page = styled.div`margin:1.5rem 1rem 3rem;position:relative;@media(max-width:640px){margin:0 0 2rem;}`;

const Hero = styled.div`position:relative;background:#fff;box-shadow:0 25px 50px -12px rgba(0,0,0,.18);`;
// Below 859px, PhotoWrap pulls the avatar up over the Hero by a fixed amount
// (see its own comment) - on desktop the 15rem min-height below keeps plenty
// of clearance under the headline text no matter what, but that min-height
// is dropped on a phone since the headline block would otherwise leave
// awkward empty space. Extra bottom padding (rather than a min-height, which
// could still be too short on a narrow phone where the tagline wraps to an
// extra line) reserves that same clearance regardless of how many lines the
// headline text wraps to - without it the pulled-up avatar overlaps the
// subheadline/tagline text. Verified at 316-390px: a fixed ~40px gap between
// the tagline and the avatar holds across that whole range.
const HeroInner = styled.div`
  max-width:78rem; margin:0 auto; padding:3.75rem 2.5rem 3.25rem; min-height:15rem;
  @media(max-width:600px){ padding:2.5rem 1.5rem 7.5rem; min-height:0; }
`;
const BigHeadline = styled.h1`margin:0;color:${BLUE};font-weight:800;text-transform:uppercase;letter-spacing:.02em;line-height:1.04;font-size:clamp(2rem,4.4vw,3.4rem);`;
const SubHeadline = styled.p`margin:.85rem 0 0;color:#5B5B5B;font-weight:700;font-size:clamp(1rem,1.9vw,1.35rem);`;
// max-width keeps the tagline out of the avatar's overlap zone (the photo is
// pulled up over the hero's bottom - see PhotoWrap) so its last wrapped line
// can't run underneath the profile picture.
const TagLine = styled.p`margin:.5rem 0 0;color:#5B5B5B;font-weight:400;font-style:italic;font-size:clamp(.85rem,1.3vw,1rem);max-width:40rem;`;

const Grey = styled.div`background:#d0e6ef;`;
// Four named grid areas instead of two flex columns: topLeft (banners +
// motivation) and topRight (photo) share row 1, bottomLeft (RecommendedStudies)
// and bottomRight (event history) share row 2. A CSS grid row is exactly as
// tall as its tallest cell, so row 2 always starts at the same y in both
// columns no matter how tall topLeft's banners get that day - no more fixed
// min-height calibration needed to keep "HEARTPORT EXPERT" and "HEARTS
// Conferences" aligned. align-items:start keeps the photo pinned to the top
// of row 1 too, so it never drifts down when a banner appears above it.
const GreyInner = styled.div`
  position:relative; max-width:78rem; margin:0 auto; padding:2rem 2.5rem;
  display:grid; align-items:start; column-gap:2.75rem; row-gap:1.75rem;
  grid-template-columns:1fr 27rem;
  grid-template-areas:"topLeft topRight" "bottomLeft bottomRight";
  @media(max-width:859px){
    grid-template-columns:1fr; gap:1.5rem; padding:1.5rem;
    grid-template-areas:"topRight" "bottomRight" "topLeft" "bottomLeft";
  }
`;
const TopLeft = styled.div`grid-area:topLeft;min-width:0;@media(min-width:860px){padding-top:1.25rem;}`;
const TopRight = styled.div`grid-area:topRight;`;
const BottomLeft = styled.div`grid-area:bottomLeft;min-width:0;`;
const BottomRight = styled.aside`grid-area:bottomRight;`;

const MotivationLabel = styled.div`
  display:inline-block;font-size:.68rem;font-weight:800;letter-spacing:.05em;text-transform:uppercase;color:#5B6B78;
  padding-bottom:6px;background-image:linear-gradient(to right,#4e4094,#14cbde);background-size:100% 2px;background-position:0 bottom;background-repeat:no-repeat;
`;
const MotivationText = styled.p`font-size:.92rem;font-style:italic;color:#334155;margin:.3rem 0 0;line-height:1.5;max-width:34rem;`;

const PhotoWrap = styled.div`
  display:flex;flex-direction:column;align-items:center;text-align:center;margin-top:-130px;
  @media(max-width:859px){ margin-top:-104px; }
`;
const PhotoButton = styled.button`position:relative;border:0;background:none;padding:0;cursor:pointer;border-radius:50%;&:hover .edit-badge{transform:scale(1.08);}`;
const Photo = styled.img`width:190px;height:190px;border-radius:50%;object-fit:cover;border:5px solid ${BLUE};background:#eef2f7;`;
const Initials = styled.div`width:190px;height:190px;border-radius:50%;border:5px solid ${BLUE};background:#eef2f7;display:flex;align-items:center;justify-content:center;font-size:3.2rem;font-weight:800;color:${BLUE};`;
const EditBadge = styled.span`position:absolute;bottom:8px;right:8px;width:34px;height:34px;border-radius:50%;background:${BLUE};color:#fff;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(15,23,42,.3);border:2px solid #fff;transition:transform .2s;`;
const Name = styled.div`margin-top:.75rem;font-weight:800;font-size:1.2rem;color:#111D4E;`;
const TitleLine = styled.div`color:#5B5B5B;font-size:.9rem;margin-top:.15rem;word-break:break-word;`;

const Nudge = styled.div`background:#eff6ff;border:1px solid #bfdbfe;color:#1e3a8a;padding:.85rem 1rem;margin-bottom:1.5rem;font-size:.9rem;display:flex;flex-wrap:wrap;gap:.6rem;align-items:center;justify-content:space-between;`;
const NudgeLink = styled.button`background:none;border:0;padding:0;color:#1e3a8a;font-weight:700;text-decoration:underline;cursor:pointer;font-size:.9rem;`;

// Same plain headline treatment as RecommendedStudies.js's own section
// title (bold caps + italic subtitle, no background box), so the two
// sections in this two-column layout read as one visual system.
const HistoryHeader = styled.div`margin:0 0 1rem;`;
const HistoryTitle = styled.div`color:${BLUE};font-weight:800;text-transform:uppercase;letter-spacing:.04em;font-size:1.35rem;`;
const HistorySubtitle = styled.div`color:#5B5B5B;font-weight:400;font-style:italic;font-size:.92rem;margin-top:.2rem;`;
// $past fades the whole card (thumbnail included) so a glance down the list
// separates what's already happened from what's still upcoming, without
// touching the real outcome status (Confirmed/Declined/...) next to it -
// see PastTag below for that distinction.
const RegCard = styled.div`display:flex;flex-direction:column;background:#fff;padding:.9rem;margin-bottom:.8rem;box-shadow:0 6px 16px rgba(15,23,42,.06);${p => (p.$past ? 'opacity:.6;' : '')}`;
const CardTop = styled.div`display:flex;gap:1rem;align-items:center;`;
const Thumb = styled.img`width:112px;height:82px;object-fit:cover;flex:none;background:#eef2f7;`;
const ThumbFallback = styled.div`width:112px;height:82px;flex:none;background:#eef2f7;display:flex;align-items:center;justify-content:center;color:#94a3b8;`;
const RegInfo = styled.div`min-width:0;`;
const RegName = styled.div`font-weight:700;color:#111D4E;font-size:1.02rem;`;
const RegMeta = styled.div`color:#5B6B78;font-size:.85rem;margin-top:.12rem;`;
const STATUS_COLORS = {
  CONFIRMED: ['#dcfce7', '#166534'], ACCEPTED: ['#dcfce7', '#166534'],
  DECLINED: ['#fee2e2', '#991b1b'], REJECTED: ['#fee2e2', '#991b1b'], CANCELLED: ['#fee2e2', '#991b1b'],
  WITHDRAWN: ['#f1f5f9', '#64748b'],
};
const StatusTag = styled.span`display:inline-block;margin-top:.35rem;padding:.12rem .55rem;font-size:.72rem;font-weight:700;background:${p => (STATUS_COLORS[p.$status] || ['#e0f2fe'])[0]};color:${p => (STATUS_COLORS[p.$status] || [null, '#075985'])[1]};`;
const RoleTag = styled.span`display:inline-block;padding:.1rem .5rem;font-size:.68rem;font-weight:800;letter-spacing:.03em;text-transform:uppercase;background:#111D4E;color:#fff;margin-right:.4rem;`;
const PastTag = styled.span`display:inline-block;margin-top:.35rem;margin-left:.4rem;padding:.12rem .55rem;font-size:.72rem;font-weight:700;background:#e5e7eb;color:#6b7280;`;
const RegSummary = styled.div`color:#334155;font-size:.85rem;margin-top:.3rem;font-style:italic;`;
const RegLink = styled(Link)`display:inline-block;margin-top:.4rem;color:#0369a1;font-size:.8rem;font-weight:600;text-decoration:none;&:hover{text-decoration:underline;}`;
const Empty = styled.p`color:#5B6B78;`;

// Event thumbnail with graceful fallback (missing URL or image that fails to load) - copied from SpeakerDashboard.js's EventThumb.
const EventThumb = ({ url }) => {
  const [failed, setFailed] = useState(false);
  if (!url || failed) return <ThumbFallback><Calendar size={22} /></ThumbFallback>;
  return <Thumb src={`${API}${url}`} alt="" referrerPolicy="no-referrer" onError={() => setFailed(true)} />;
};

const VerifyNotice = styled.div`background:#fef3c7;color:#92400e;padding:.85rem 1rem;margin-bottom:1.5rem;font-size:.9rem;display:flex;flex-wrap:wrap;gap:.6rem;align-items:center;justify-content:space-between;`;
const VerifyLink = styled.button`background:none;border:0;padding:0;color:#92400e;font-weight:700;text-decoration:underline;cursor:pointer;font-size:.9rem;`;

const RegisteredBanner = styled.div`background-color:#d1fae5;color:#065f46;padding:1rem;margin-bottom:1.5rem;text-align:center;`;
const Message = styled.p`font-size:.875rem;color:${p => (p.error ? '#b91c1c' : '#065f46')};`;

const AttendeeProfile = () => {
  const intl = useIntl();
  const t = (id, defaultMessage, values) => intl.formatMessage({ id, defaultMessage }, values);
  const location = useLocation();
  // Set by LoginForm.js when landing here right after a login that also
  // completed an event registration (see registerForEvent in authUtils.js) -
  // the only path where that registration would otherwise finish with no
  // visible confirmation anywhere in the UI.
  const registeredEventName = location.state?.registeredEventName;
  const { language } = useLanguage();
  const countryOptions = useMemo(() => getCountryOptions(language), [language]);
  const [profile, setProfile] = useState(null);
  const [history, setHistory] = useState([]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [verificationMessage, setVerificationMessage] = useState(null);

  useEffect(() => {
    api.get('/api/attendee/profile')
      .then(({ data }) => setProfile(data))
      .catch(() => setProfile({ error: true }));
    api.get('/api/account/history')
      .then(({ data }) => setHistory(data))
      .catch(() => setHistory([]));
  }, []);

  const openEdit = () => { setDraft(profile); setMessage(null); setEditing(true); };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDraft({ ...draft, [name]: value });
  };

  const toggleDraftList = (key, value) => {
    setDraft((current) => ({
      ...current,
      [key]: (current[key] || []).includes(value)
        ? current[key].filter((v) => v !== value)
        : [...(current[key] || []), value],
    }));
  };

  const roleLabel = (status) => t(`speaker.role.${status}`, status.replaceAll('_', ' '));

  const handleSave = async (e) => {
    e.preventDefault();
    setMessage(null);
    if ((draft.interests || []).length < MIN_INTERESTS) {
      setMessage({ text: t('speaker.onboarding.err.interests', 'Please select at least three cardiac interests.'), error: true });
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put('/api/attendee/profile', draft);
      setProfile(data);
      setEditing(false);
    } catch (err) {
      console.error('Profile save failed:', err);
      setMessage({ text: describeSaveError(err, t), error: true });
    } finally {
      setSaving(false);
    }
  };

  const handleResendVerification = async () => {
    setVerificationMessage(null);
    try {
      await api.post('/api/attendee/resend-verification');
      setVerificationMessage(t('profile.verificationSent', 'Verification email sent - please check your inbox.'));
    } catch (err) {
      setVerificationMessage(t('profile.verificationError', 'Could not send verification email.'));
    }
  };

  const formatDate = (v) => (v ? new Date(v).toLocaleString(intl.locale, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
  const initials = (fn, ln) => [fn, ln].filter(Boolean).map((w) => w[0]?.toUpperCase()).join('') || '·';
  const statusLabel = (s) => t(`profile.status.${s}`, s);
  const isPastEvent = (v) => Boolean(v) && new Date(v) < new Date();
  const roleLabelFor = (role) => t(`profile.history.role.${role}`, role);
  // SUBMITTER carries the specific engagement id so SubmissionsPage can load
  // this exact submission into the edit form instead of the blank "new
  // submission" one - a person can have several submissions, so just
  // linking to /submissions with no id landed on the wrong (empty) state.
  const manageLink = (entry) => (entry.role === 'SPEAKER' ? '/speaker/dashboard' : entry.role === 'SUBMITTER' ? `/submissions?edit=${entry.id}` : null);
  const manageLinkLabel = (role) => t(`profile.history.manage.${role}`, 'Manage');

  if (!profile) {
    return <Page><Hero><HeroInner><Empty>{t('profile.loading', 'Loading your profile…')}</Empty></HeroInner></Hero></Page>;
  }

  if (profile.error) {
    return <Page><Hero><HeroInner><Empty>{t('profile.loadError', 'Your profile could not be loaded.')}</Empty></HeroInner></Hero></Page>;
  }

  // The account title is a name prefix (Mr./Ms./Dr./Prof. - the signup and
  // edit forms' title options), so it belongs on the name line under the
  // photo, not on the specialty line. The forms' explicit "no title" choices
  // store a literal sentinel ('No title'/'keine Angabe') which is never
  // rendered - same convention as UserManagement.js's fullName().
  const displayName = [profile.title, profile.firstName, profile.lastName]
    .filter((v) => v && v !== 'No title' && v !== 'keine Angabe')
    .join(' ') || profile.email;
  const titleLine = [profile.specialty, profile.institution].filter(Boolean).join(' · ');
  const motivation = (intl.locale || '').startsWith('de') ? (profile.motivationDe || profile.motivationEn) : (profile.motivationEn || profile.motivationDe);
  const needsCompletion = (profile.interests || []).length < MIN_INTERESTS || (profile.professionalStatuses || []).length === 0;

  return (
    <Page>
      <Hero>
        <HeroInner>
          {/* Hardcoded (non-i18n) header copy for the universal profile page -
              requested verbatim by the customer; no longer shared with
              SpeakerDashboard.js's hero (that page keeps its own copy). */}
          <BigHeadline>YOUR PORT TO THE ART OF MEDICAL SCIENCE</BigHeadline>
          <SubHeadline>Build your best CARE with HEARTPORT.</SubHeadline>
          <TagLine>Join HEARTS conferences. Connect with experts. Keep up with research. Share your medical art. Apply AI for clinical excellence: DEMOCRATIZING SCIENCE.</TagLine>
        </HeroInner>
      </Hero>

      <Grey>
        <GreyInner>
          <TopLeft>
            {registeredEventName && (
              <RegisteredBanner>
                {t('verify.eventRegistered', "You're now registered for {eventName}.", { eventName: registeredEventName })}
              </RegisteredBanner>
            )}

            {!profile.emailVerified && (
              <VerifyNotice>
                <span>{t('profile.verifyNotice', 'Please verify your email to register for events.')}</span>
                <VerifyLink type="button" onClick={handleResendVerification}>
                  {t('profile.resendVerification', 'Resend verification email')}
                </VerifyLink>
              </VerifyNotice>
            )}
            {verificationMessage && <Message>{verificationMessage}</Message>}

            {needsCompletion && (
              <Nudge>
                <span>{t('profile.completeNudge', 'Add your topic interests so we can match you to the right sessions and panels.')}</span>
                <NudgeLink type="button" onClick={openEdit}>{t('profile.completeNudgeAction', 'Complete my profile')}</NudgeLink>
              </Nudge>
            )}

            {motivation && (
              <>
                <MotivationLabel>{t('profile.motivationLabel', 'I am part of HEARTS - DEMOCRATIZING SCIENCE - because')}</MotivationLabel>
                <MotivationText>{motivation}</MotivationText>
              </>
            )}
          </TopLeft>

          <TopRight>
            <PhotoWrap>
              <PhotoButton type="button" onClick={openEdit} aria-label={t('profile.editProfile', 'Edit my profile')}>
                {profile.photoUrl ? <Photo src={mediaUrl(profile.photoUrl)} alt="" referrerPolicy="no-referrer" /> : <Initials>{initials(profile.firstName, profile.lastName)}</Initials>}
                <EditBadge className="edit-badge"><Pencil size={16} /></EditBadge>
              </PhotoButton>
              <Name>{displayName}</Name>
              {titleLine && <TitleLine>{titleLine}</TitleLine>}
            </PhotoWrap>
          </TopRight>

          <BottomLeft>
            <RecommendedStudies t={t} />
          </BottomLeft>

          <BottomRight>
            <HistoryHeader>
              {/* Hardcoded like RecommendedStudies.js's own banner - not routed through react-intl. */}
              <HistoryTitle>HEARTS Conferences</HistoryTitle>
              <HistorySubtitle>Manage upcoming live conferences</HistorySubtitle>
            </HistoryHeader>
            {history.length === 0 ? (
              <Empty>{t('profile.noEvents', 'You are not registered for any events yet.')}</Empty>
            ) : (
              history.map((entry) => {
                const link = manageLink(entry);
                const past = isPastEvent(entry.eventDateTime);
                return (
                  <RegCard key={entry.id} $past={past}>
                    <CardTop>
                      <EventThumb url={entry.eventImageUrl} />
                      <RegInfo>
                        <RegName><RoleTag>{roleLabelFor(entry.role)}</RoleTag>{entry.eventName}</RegName>
                        <RegMeta>{[formatDate(entry.eventDateTime), entry.eventLocation].filter(Boolean).join(' · ')}</RegMeta>
                        <StatusTag $status={entry.status}>{statusLabel(entry.status)}</StatusTag>
                        {past && <PastTag>{t('profile.history.past', 'Past')}</PastTag>}
                        {entry.summary && <RegSummary>“{entry.summary}”</RegSummary>}
                        {link && <div><RegLink to={link}>{manageLinkLabel(entry.role)} →</RegLink></div>}
                      </RegInfo>
                    </CardTop>
                  </RegCard>
                );
              })
            )}
          </BottomRight>
        </GreyInner>
      </Grey>

      {editing && draft && (
        <ProfileEditModal
          draft={draft} countryOptions={countryOptions} message={message} saving={saving}
          t={t} roleLabel={roleLabel}
          onChange={handleChange} onToggleList={toggleDraftList}
          onInterestsChange={(v) => setDraft((current) => ({ ...current, interests: v }))}
          onSubmit={handleSave} onCancel={() => setEditing(false)}
        />
      )}
    </Page>
  );
};

export default AttendeeProfile;
